import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Star, ArrowRight, Plus, Minus, Quote } from "lucide-react";

export function SectionHeading({ eyebrow, title, subtitle, center, light }) {
  return (
    <div className={`${center ? "text-center mx-auto max-w-2xl" : "max-w-2xl"} reveal`}>
      {eyebrow && (
        <span className={`inline-block text-xs font-heading font-bold uppercase tracking-[0.18em] mb-3 ${light ? "text-brand-sky2" : "text-brand-ocean"}`}>
          {eyebrow}
        </span>
      )}
      <h2 className={`text-3xl md:text-[2.6rem] leading-tight font-heading font-extrabold ${light ? "text-white" : "text-brand-navy"}`}>
        {title}
      </h2>
      {subtitle && (
        <p className={`mt-4 text-base md:text-lg leading-relaxed ${light ? "text-white/70" : "text-brand-slate"}`}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

export function StarRow({ n = 5, className = "" }) {
  return (
    <div className={`flex items-center gap-0.5 ${className}`} aria-label={`${n} out of 5 stars`}>
      {Array.from({ length: n }).map((_, i) => (
        <Star key={i} className="h-4 w-4 fill-brand-gold text-brand-gold" />
      ))}
    </div>
  );
}

export function ServiceCard({ service, index = 0 }) {
  const SEO_MAP = {
    "pool-enclosures": "/pool-screen-enclosures",
    "patio-porch": "/patio-porch-screen-enclosures",
    "lanai-porch-screening": "/patio-porch-screen-enclosures",
    "rescreen-repair": "/pool-screen-repair-rescreening",
    "carports": "/carports",
    "lanai-elite": "/screen-enclosures",
  };
  const to = SEO_MAP[service.id] || "/services";
  return (
    <div
      className="group reveal relative overflow-hidden rounded-2xl bg-white shadow-card ring-1 ring-brand-mist hover:ring-brand-sky/40 transition-all duration-300 hover:-translate-y-1"
      style={{ transitionDelay: `${(index % 4) * 60}ms` }}
    >
      <div className="relative h-52 overflow-hidden">
        <img
          src={service.image}
          alt={service.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/70 via-transparent to-transparent" />
        {service.tag && (
          <span className="absolute top-3 left-3 bg-brand-gold text-brand-navy text-[11px] font-heading font-extrabold uppercase tracking-wide px-3 py-1 rounded-full shadow">
            {service.tag}
          </span>
        )}
      </div>
      <div className="p-6">
        <Link to={to}>
          <h3 className="text-xl font-heading font-extrabold text-brand-navy group-hover:text-brand-ocean transition-colors">
            {service.title}
          </h3>
        </Link>
        <p className="mt-2 text-sm text-brand-slate leading-relaxed">{service.short}</p>
        <ul className="mt-4 space-y-1.5">
          {service.features.map((f, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-brand-navy/80">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-sky" /> {f}
            </li>
          ))}
        </ul>
        <Link
          to={to}
          className="mt-5 inline-flex items-center gap-1.5 text-sm font-heading font-bold text-brand-ocean hover:gap-2.5 transition-all"
        >
          Learn More <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

export function ProcessSteps({ steps }) {
  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {steps.map((s, i) => (
        <div key={s.n} className="reveal relative" style={{ transitionDelay: `${i * 80}ms` }}>
          <div className="rounded-2xl bg-white p-7 shadow-card ring-1 ring-brand-mist h-full">
            <div className="text-5xl font-heading font-extrabold text-brand-sky/25">{s.n}</div>
            <h3 className="mt-2 text-lg font-heading font-extrabold text-brand-navy">{s.title}</h3>
            <p className="mt-2 text-sm text-brand-slate leading-relaxed">{s.text}</p>
          </div>
          {i < steps.length - 1 && (
            <ArrowRight className="hidden lg:block absolute top-1/2 -right-4 -translate-y-1/2 h-6 w-6 text-brand-sky/50" />
          )}
        </div>
      ))}
    </div>
  );
}

export function StatsBar({ stats }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {stats.map((s, i) => (
        <div
          key={i}
          className="reveal text-center rounded-2xl bg-white/10 ring-1 ring-white/15 py-7 px-4 backdrop-blur-sm"
          style={{ transitionDelay: `${i * 70}ms` }}
        >
          <div className="text-3xl md:text-4xl font-heading font-extrabold text-brand-gold2">{s.value}</div>
          <div className="mt-1 text-sm text-white/75 font-medium">{s.label}</div>
        </div>
      ))}
    </div>
  );
}

export function TestimonialCard({ t }) {
  return (
    <div className="reveal relative rounded-2xl bg-white p-7 shadow-card ring-1 ring-brand-mist h-full flex flex-col">
      <Quote className="h-8 w-8 text-brand-sky/25" />
      <StarRow n={t.rating} className="mt-3" />
      <p className="mt-3 text-brand-navy/85 leading-relaxed flex-1">"{t.quote}"</p>
      <div className="mt-5 pt-4 border-t border-brand-mist">
        <div className="font-heading font-extrabold text-brand-navy">{t.name}</div>
        <div className="text-sm text-brand-slate">{t.city}</div>
      </div>
    </div>
  );
}

export function FAQAccordion({ faqs }) {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <div className="space-y-3">
      {faqs.map((f, i) => {
        const isOpen = openIdx === i;
        return (
          <div key={i} className="reveal">
            <div
              className={`rounded-xl ring-1 transition-colors ${
                isOpen ? "bg-white ring-brand-sky/40 shadow-card" : "bg-white ring-brand-mist"
              }`}
            >
            <button
              onClick={() => setOpenIdx(isOpen ? -1 : i)}
              aria-expanded={isOpen}
              className="w-full flex items-center justify-between gap-4 text-left px-5 py-4"
            >
              <span className="font-heading font-bold text-brand-navy">{f.q}</span>
              <span className={`shrink-0 grid place-items-center h-8 w-8 rounded-full transition-colors ${isOpen ? "bg-brand-ocean text-white" : "bg-brand-mist text-brand-ocean"}`}>
                {isOpen ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              </span>
            </button>
            <div className={`grid transition-all duration-300 ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
              <div className="overflow-hidden">
                <p className="px-5 pb-5 text-brand-slate leading-relaxed">{f.a}</p>
              </div>
            </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
